import { Subscription } from '@rails/actioncable';
import { getCableConsumer } from './CableClient';
import type {
  InitialStateMessage,
  PrivateInitialStateMessage,
  GameUpdateMessage,
  PrivateUpdateMessage,
  GameFinishedMessage,
  ErrorMessage,
  SessionAbandonedMessage,
  RematchStateMessage,
  GameChannelMessage,
} from '../types/game';

type MessageCallback<T> = (data: T) => void;

/**
 * GameChannel manages the ActionCable subscription for a specific game.
 *
 * The server streams to TWO channels per user:
 *   - "game_{id}" (public: game_update, game_finished, initial_state)
 *   - "game_{id}_user_{userId}" (private: private_update, private_initial_state)
 *
 * The client subscribes ONCE with { game_id } and receives both streams.
 */
export class GameChannel {
  private subscription: Subscription | null = null;
  private gameId: number;
  private sceneId: number;  // GameScene instance id — for log correlation

  // Callbacks
  private _onInitialState: MessageCallback<InitialStateMessage> | null = null;
  private _onPrivateInitialState: MessageCallback<PrivateInitialStateMessage> | null = null;
  private _onGameUpdate: MessageCallback<GameUpdateMessage> | null = null;
  private _onPrivateUpdate: MessageCallback<PrivateUpdateMessage> | null = null;
  private _onGameFinished: MessageCallback<GameFinishedMessage> | null = null;
  private _onRematchState: MessageCallback<RematchStateMessage> | null = null;
  private _onSessionAbandoned: MessageCallback<SessionAbandonedMessage> | null = null;
  private _onError: MessageCallback<ErrorMessage> | null = null;
  private _onReconnect: (() => void) | null = null;
  private _onDisconnect: (() => void) | null = null;
  private _onRejected: (() => void) | null = null;

  // Track whether the first connection has already happened
  private _everConnected = false;

  constructor(gameId: number, sceneId = 0) {
    this.gameId = gameId;
    this.sceneId = sceneId;
  }

  private get tag() { return `[GameChannel scene#${this.sceneId} game=${this.gameId}]`; }

  /** Subscribe to the game channel */
  async subscribe(): Promise<void> {
    console.log(`${this.tag} subscribe()`);
    const consumer = await getCableConsumer();

    this.subscription = consumer.subscriptions.create(
      { channel: 'GameChannel', game_id: this.gameId },
      {
        received: (data: unknown) => {
          const msg = data as GameChannelMessage;
          console.log(`${this.tag} received type=${msg.type}`);
          this.handleMessage(msg);
        },
        connected: () => {
          if (this._everConnected) {
            // This is a reconnect — notify GameScene so it can re-sync state
            console.log(`${this.tag} reconnected — triggering state refresh`);
            this._onReconnect?.();
          } else {
            console.log(`${this.tag} connected (first time)`);
            this._everConnected = true;
          }
        },
        disconnected: () => {
          console.log(`${this.tag} disconnected`);
          this._onDisconnect?.();
        },
        rejected: () => {
          console.error(`${this.tag} subscription REJECTED`);
          this._onRejected?.();
        },
      }
    );
    console.log(`${this.tag} subscription object created`);
  }

  /** Handle incoming messages and dispatch to callbacks */
  private handleMessage(data: GameChannelMessage): void {
    switch (data.type) {
      case 'initial_state':
        this._onInitialState?.(data);
        break;
      case 'private_initial_state':
        this._onPrivateInitialState?.(data);
        break;
      case 'game_update':
        this._onGameUpdate?.(data);
        break;
      case 'private_update':
        this._onPrivateUpdate?.(data);
        break;
      case 'game_finished':
        this._onGameFinished?.(data);
        break;
      case 'rematch_state':
        this._onRematchState?.(data);
        break;
      case 'session_abandoned':
        this._onSessionAbandoned?.(data);
        break;
      case 'error':
        this._onError?.(data);
        break;
      default:
        console.warn(`${this.tag} unknown message type:`, data);
    }
  }

  // ─── Register Callbacks ──────────────────────────────────

  onInitialState(callback: MessageCallback<InitialStateMessage>): this {
    this._onInitialState = callback;
    return this;
  }

  onPrivateInitialState(callback: MessageCallback<PrivateInitialStateMessage>): this {
    this._onPrivateInitialState = callback;
    return this;
  }

  onGameUpdate(callback: MessageCallback<GameUpdateMessage>): this {
    this._onGameUpdate = callback;
    return this;
  }

  onPrivateUpdate(callback: MessageCallback<PrivateUpdateMessage>): this {
    this._onPrivateUpdate = callback;
    return this;
  }

  onGameFinished(callback: MessageCallback<GameFinishedMessage>): this {
    this._onGameFinished = callback;
    return this;
  }

  /** Final-match rematch vote updates (whole-match rematch after target score). */
  onRematchState(callback: MessageCallback<RematchStateMessage>): this {
    this._onRematchState = callback;
    return this;
  }

  onSessionAbandoned(callback: MessageCallback<SessionAbandonedMessage>): this {
    this._onSessionAbandoned = callback;
    return this;
  }

  onError(callback: MessageCallback<ErrorMessage>): this {
    this._onError = callback;
    return this;
  }

  /**
   * Called every time the WebSocket reconnects after a drop.
   * Use this to re-fetch game state so nothing is missed.
   */
  onReconnect(callback: () => void): this {
    this._onReconnect = callback;
    return this;
  }

  /** Called whenever the WebSocket drops. */
  onDisconnect(callback: () => void): this {
    this._onDisconnect = callback;
    return this;
  }

  /**
   * Called when the server REJECTS the subscription (membership/auth failure).
   * Game-critical: without this the board would hydrate from REST and then
   * silently never receive updates. The consumer (GameScene) uses it to route
   * to a safe recovery instead of freezing.
   */
  onRejected(callback: () => void): this {
    this._onRejected = callback;
    return this;
  }

  // ─── Send Actions ────────────────────────────────────────

  /** Play a tile via WebSocket */
  playTile(tileId: number, position: 'left' | 'right'): void {
    if (!this.subscription) {
      console.error('[GameChannel] Not subscribed — cannot play tile');
      return;
    }
    this.subscription.perform('play_tile', {
      tile_id: tileId,
      position,
    });
  }

  /** Pass turn via WebSocket */
  passTurn(): void {
    if (!this.subscription) {
      console.error('[GameChannel] Not subscribed — cannot pass turn');
      return;
    }
    this.subscription.perform('pass_turn', {});
  }

  // ─── Lifecycle ───────────────────────────────────────────

  /** Unsubscribe from the channel */
  unsubscribe(): void {
    console.log(`${this.tag} unsubscribe()  hadSubscription=${this.subscription !== null}`);
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
    this._onInitialState = null;
    this._onPrivateInitialState = null;
    this._onGameUpdate = null;
    this._onPrivateUpdate = null;
    this._onGameFinished = null;
    this._onRematchState = null;
    this._onSessionAbandoned = null;
    this._onError = null;
    this._onReconnect = null;
    this._onDisconnect = null;
    this._onRejected = null;
  }
}
